import { Flex, Text } from '@servicetitan/anvil2';
import { Calculation } from '@servicetitan/financing-api-client';
import { formatCurrency } from '@servicetitan/financing-utils-shared';
import { PlanCard } from './PlanCard';
import { PreApprovalDisplay } from './usePreApprovalDisplay';
import { AmountRangePreApprovalDisplay } from './preApprovalDisplay';
import preApprovalBadge from './pre-approval-badge.svg';
import * as styles from './PreApprovalCard.module.scss';

interface PreApprovalCardProps {
    /** The resolved pre-approval display state. Only the renderable kinds carry a calculation. */
    display: Extract<
        PreApprovalDisplay | AmountRangePreApprovalDisplay,
        { kind: 'visible' | 'outOfRange' | 'unavailable' }
    >;
    /** Wrapped by `Container` to set `setupData.preApprovalId` before delegating to the workflow. */
    onPlanSelected?: (plan: Calculation) => void;
}

// Layered FCRA short notice. Conspicuous UI copy (not provider legal text) that forward-references
// the full PRESCREEN & OPT-OUT NOTICE rendered below; the phone number is the statutory opt-out line.
const OPT_OUT_COPY =
    'You can choose to stop receiving "prescreened" offers of credit from this and other ' +
    'companies by calling toll-free 1.888.567.8688. See PRESCREEN & OPT-OUT NOTICE below for ' +
    'more information about prescreened offers.';

/**
 * The GreenSky pre-approval card: a promotional message on the left and a standard `PlanCard` for
 * the pre-approved plan on the right (stacked into one column at the narrow breakpoint). Out of
 * range, the same wrap/title/opt-out copy stay and the inner `PlanCard` switches to its
 * `unavailable` form.
 *
 * Uses a styled `div` (not an Anvil `Card`) laid out with a CSS grid. The narrow breakpoint drops
 * the border/rounding for a full-bleed band (an Anvil `Card` cannot), and the wide breakpoint sizes
 * the plan card in a fixed track equal to the selector's grid columns, so the inline-flex `Card`
 * stretches to exactly the same width as the cards in the selector below. The layout switches on the
 * same container query (against `.plans-container`) that the selector grid uses, so the card and the
 * selector always change layout together.
 */
export const PreApprovalCard = ({ display, onPlanSelected }: PreApprovalCardProps) => {
    const { preApproval, calculation, kind } = display;

    // Pre-approval limit (not the financed amount): whole dollars unless cents are present.
    const amount = formatCurrency(preApproval.amount!, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    });

    return (
        <div className={styles.container}>
            <div className={styles.layout}>
                <Flex direction="column" gap="2">
                    <Flex direction="column" gap="2" alignItems="flex-start">
                        <img src={preApprovalBadge} alt="" width={32} height={32} />
                        <Text variant="headline" el="h3" className={styles.heading}>
                            You&apos;re pre-approved for up to {amount} in financing with GreenSky®
                            Program.
                        </Text>
                    </Flex>
                    <Text variant="body" subdued>
                        {OPT_OUT_COPY}
                    </Text>
                </Flex>
                <PlanCard
                    plan={calculation}
                    showLowestPayment={false}
                    disableSelection={false}
                    unavailable={kind === 'outOfRange' || kind === 'unavailable'}
                    onPlanSelected={onPlanSelected}
                />
            </div>
        </div>
    );
};
